import { useEffect, useState } from 'react';
import { Grid, Paper } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import { useSaveData } from '../../hooks/useSaveData';
import { FiltersComponent } from './filters.component';
import { CalendarComponent } from './calendar.component';
import { InspectorComponent } from './inspectors.component';
import { SiTrackPage } from '../../components/si-track-page';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { inspectorApiKeyStore } from '../../api/inspector-api-key-store';
import { SelectResult } from '../../components/select/select-chain.component';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { usersApiKeyStore } from '../../api/users-api-key-store';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
  };
};

const styling = {
  paperStyle: { padding: '15px', borderRadius: '10px' },
};

const isMobileViewEnabled = process.env.REACT_APP_MOBILE_VIEW_FLAG === 'true';

function getRandomDarkColor() {
  const r = Math.floor(Math.random() * 101) + 30;
  const g = Math.floor(Math.random() * 105) + 50;
  const b = Math.floor(Math.random() * 115) + 60;

  const hexR = r.toString(16).padStart(2, '0');
  const hexG = g.toString(16).padStart(2, '0');
  const hexB = b.toString(16).padStart(2, '0');

  return `#${hexR}${hexG}${hexB}`;
}

export const InspectorCalendarPage = () => {
  const [searchParams] = useSearchParams();
  const [scheduleId, setScheduleId] = useState<any>();
  const [inspectorColors, setInspectorColors] = useState<any>();
  const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));
  const [inspectorData, setInspectorData] = useState<any>([]);
  const [storeEventData, setStoreEventData] = useState<any>([]);
  const [workorderEventData, setWorkOrderEventData] = useState<any>([]);

  const form = useReactHookForm({});
  const isViewTeamCalender = useAuthorize([UserPermissionName.VIEW_TEAM_CALENDAR]);

  const { isLoading, data, mutateAsync } = useSaveData<any>(inspectorApiKeyStore.inspectionsEvents());

  const { data: userData, isLoading: isUserLoading } = useLoadData<ApiResponse<any>>(usersApiKeyStore.getUserDetails());

  useEffect(() => {
    if (isMobile && isMobileViewEnabled) {
      mutateAsync({ data: {} });
    }
  }, [isMobile, isMobileViewEnabled]);

  useEffect(() => {
    if (data) {
      const colors: any = {};
      data?.data.inspectorDetails.forEach((inspector: any) => {
        colors[inspector.inspectorID] = getRandomDarkColor();
      });
      setInspectorColors(colors);
      setInspectorData(data?.data?.inspectorDetails);
      setStoreEventData(data?.data?.woEvents);
      setWorkOrderEventData(data?.data?.woEvents);
    }
  }, [data]);

  const handleSubmit = (filtersData: any) => {
    const filteringData = {
      customerID: filtersData?.customer?.customerID,
      offices: filtersData?.officeIDs?.map((p: any) => p.officeID),
      projects: filtersData?.projectIDs?.map((p: any) => p.projectID),
      marketAreas: filtersData?.marketAreaIDs?.map((p: any) => p.marketAreaID),
      inspectors: filtersData?.userIDs?.map((p: any) => p.userID),
      workOrderStatusIDs: filtersData?.workOrderStatusID?.map((s: any) => s?.workOrderStatusID),
      scheduledDateFrom: filtersData?.fromDate,
      scheduledDateTo: filtersData?.toDate,
      divisions: filtersData?.divisions?.map((p: any) => p.divisionID),
      isManagerView: !isViewTeamCalender ? false : true,
    };
    mutateAsync({ data: filteringData });
    setSelectedFilters(filteringData);
  };

  const showSingleInspector = (inspectorID: any) => {
    const filterEvent = storeEventData.filter((eventData: any) => Number(eventData.inspectorID) === inspectorID);
    setWorkOrderEventData(filterEvent);
  };

  const handleStatusId = (statusId: any) => {
    setScheduleId(statusId);
  };

  return (
    <SiTrackPage title="Inspector Calendar">
      {!isMobile || !isMobileViewEnabled ? (
        <ReactHookForm formName="INSPECTOR_CALENDAR_FORM" onSubmit={handleSubmit} formContextProps={form}>
          <Grid container spacing={2}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <FiltersComponent setStatusId={handleStatusId} />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Paper elevation={0} style={styling.paperStyle}>
                <Grid container spacing={2}>
                  <Grid item lg={4} sm={4} xl={4} xs={4}>
                    <InspectorComponent
                      inspectorData={inspectorData ?? []}
                      colorCode={inspectorColors}
                      scheduleId={scheduleId}
                      selectSingleInspector={showSingleInspector}
                      isLoading={isLoading}
                      filtersData={selectedFilters}
                    />
                  </Grid>
                  <Grid item lg={8} sm={8} xl={8} xs={8}>
                    <CalendarComponent
                      workorderEvent={workorderEventData ?? []}
                      height={715}
                      colorCode={inspectorColors}
                      isLoading={isLoading}
                      calendarView={userData?.data?.roles?.includes('Inspector') ? 'timeGridDay' : 'dayGridMonth'}
                      isMobileView={isMobile && isMobileViewEnabled}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </ReactHookForm>
      ) : (
        <Paper elevation={0} style={styling.paperStyle}>
          <CalendarComponent
            workorderEvent={workorderEventData ?? []}
            height={715}
            colorCode={inspectorColors}
            isLoading={isLoading}
            isMobileView={isMobile && isMobileViewEnabled}
            calendarView="timeGridDay"
            position="bottom-start"
          />
        </Paper>
      )}
    </SiTrackPage>
  );
};
